import CustomerServicePopup, { GET_SALES_REP } from "./CustomerServicePopup";
import {
  WHAT_TO_EXPECT_FROM_ORDER_TITLE,
  WhatToExpectFromOrderModal,
} from "./WhatToExpectFromOrderModal";
import { faEnvelope, faPhoneFlip } from "@fortawesome/free-solid-svg-icons";
import {
  selectDepartmentType,
  selectHospitalId,
  selectUserRole,
} from "../../../src/redux/app";

import { AccountRepresentative } from "./AccountRepresentative";
import ContactUsDME from "./ContactUsDME";
import ContactUsGetInTouch from "./ContactUsGetInTouch";
import { DepartmentType } from "./__generated__/graphql";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PROVIDER_PORTAL_MAIN_COLUMN_CLASS } from "./util/providerPortalMainColumn";
import { TextButton } from "@verse_core/shared_components/buttons/TextButton";
import { rebrandEnabled } from "./util/brandingFlag";
import { useAppSelector } from "../../../src/redux/hooks";
import { useQuery } from "@apollo/client";
import { useRudderStackAnalytics } from "../useAnalytics";
import { useState } from "react";

/**
 * Creates then localizes working hours to the browser's locale.
 *
 * @returns A string representing the localized working hours in the format "start time - end time (time zone short name)".
 */
export const getLocalizedHoursString = (startTime: number) => {
  // Build times in America/New_York so DST is handled automatically.
  // Parse a locale string to get the current ET offset, then set
  // Date objects at the correct UTC instant
  const now = new Date();
  const etNow = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
  const etOffsetMs = etNow.getTime() - now.getTime();
  const browserLocale = navigator.language;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const localizedStartTime = getLocalizedHour(
    now,
    etOffsetMs,
    browserLocale,
    userTimeZone,
    startTime,
    false,
  );
  const localizedEndTime = getLocalizedHour(
    now,
    etOffsetMs,
    browserLocale,
    userTimeZone,
    18,
    true,
  );

  return `${localizedStartTime} - ${localizedEndTime}`;
};

export const getLocalizedHour = (
  now: Date,
  etOffsetMs: number,
  browserLocale: string,
  userTimeZone: string,
  hour: number,
  isEndTime: boolean,
): string => {
  const time = new Date(now);
  time.setHours(hour, 0, 0, 0);
  time.setTime(time.getTime() - etOffsetMs);
  const localizedTime = time.toLocaleTimeString(browserLocale, {
    hour: "numeric",
    minute: "numeric",
    timeZone: userTimeZone,
  });

  if (isEndTime) {
    const timeZoneShortName = time
      .toLocaleTimeString(browserLocale, { timeZoneName: "short" })
      .split(" ")[2];

    return `${localizedTime} ${timeZoneShortName}`;
  }
  return `${localizedTime}`;
};

const FAQ_ITEMS = [
  {
    question: "How can I send fax orders?",
    answer:
      "For progress notes or signature requests, fax to (833) 672-2753. For placing new orders, fax to (833) 694-1477.",
  },
  {
    question: "I have a question about a specific order. Where should I ask?",
    answer:
      "Just reply directly in your order message thread—our team monitors these and will get back to you.",
  },
  {
    question:
      "My order is on hold because of a progress note issue. Where can I find documentation guidelines?",
    answer: (
      <>
        Check out our{" "}
        <a
          href="/medicare-documentation-guide.pdf"
          className="tw-text-blue-1 tw-underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          cheat sheet
        </a>{" "}
        for guidance. If you still have questions about your specific order,
        just reply in the message thread and our clinical reviewers will be
        happy to help.
      </>
    ),
  },
];

const ContactUs = () => {
  const role = useAppSelector(selectUserRole);
  const isHandOffPartnerRole = role === "hand_off_partner";
  const hospitalId = useAppSelector(selectHospitalId);
  const { data: salesRepData } = useQuery(GET_SALES_REP, {
    fetchPolicy: "no-cache",
    variables: { hospitalId: hospitalId ?? 0 },
    skip: !hospitalId || isHandOffPartnerRole,
  });
  const salesRep = salesRepData?.getAccountRep ?? null;
  const isCustomerSuccessTeam = salesRep?.customerSuccessTeam ?? false;
  const departmentType = useAppSelector(selectDepartmentType);
  const isCaseManagementOrEnterprise =
    departmentType === DepartmentType.CaseManagement ||
    departmentType === DepartmentType.Enterprise;
  const isAEOwnedOrSpecialDept =
    isCaseManagementOrEnterprise ||
    (salesRep != null && !isCustomerSuccessTeam);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [showExpectationsModal, setShowExpectationsModal] = useState(false);
  const analytics = useRudderStackAnalytics();
  const rebrand = rebrandEnabled();

  if (isHandOffPartnerRole) return <ContactUsDME />;
  if (isAEOwnedOrSpecialDept) return <ContactUsGetInTouch />;

  return (
    <div
      className={`${PROVIDER_PORTAL_MAIN_COLUMN_CLASS} tw-flex tw-flex-col tw-gap-[40px] tw-py-[48px]`}
    >
      {/* Header */}
      <h1 className="tw-m-0 tw-font-domine tw-text-[36px] tw-font-medium tw-leading-none tw-tracking-[-0.36px] tw-text-blue-dark-1">
        Contact Us
      </h1>

      {/* Provider + patient support — only shown for CX accounts or accounts not owned by a rep */}
      {(isCustomerSuccessTeam || !salesRep) && (
        <div className="tw-grid tw-grid-cols-1 tw-gap-[24px] lg:tw-grid-cols-2">
          {/* Provider support */}
          <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-[24px] tw-rounded-lg tw-bg-gray-light-3 tw-p-[24px]">
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
              <div className="tw-flex tw-flex-col tw-gap-[28px]">
                <div className="tw-flex tw-flex-col tw-gap-[8px]">
                  <h2 className="tw-m-0 tw-font-degular tw-text-[28px] tw-font-medium tw-leading-none tw-tracking-[-0.28px] tw-text-blue-dark-1">
                    Provider support on demand
                  </h2>
                  <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                    For provider questions on placing orders, technical issues,
                    or general requests.
                  </p>
                </div>

                <div className="tw-flex tw-items-start tw-gap-[8px]">
                  <div className="tw-flex tw-h-[32px] tw-w-[32px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-dark-1">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="tw-text-[14px] tw-text-gray-light-3"
                    />
                  </div>
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[8px]">
                    <p className="tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-[8px] tw-text-[18px] tw-font-medium tw-text-blue-dark-1">
                      <a
                        href="mailto:provider-support@versemedical.com"
                        className="tw-font-bold tw-text-blue-dark-1 tw-underline"
                      >
                        provider-support@versemedical.com
                      </a>
                      <span className="tw-rounded-full tw-bg-gray-light-1 tw-px-[8px] tw-py-[2px] tw-text-[12px] tw-font-medium tw-text-gray-1">
                        Fastest
                      </span>
                    </p>
                    <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                      We closely monitor this inbox and respond in a few hours
                      or less.
                    </p>
                  </div>
                </div>

                <div className="tw-flex tw-items-start tw-gap-[8px]">
                  <div className="tw-flex tw-h-[32px] tw-w-[32px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-dark-1">
                    <FontAwesomeIcon
                      icon={faPhoneFlip}
                      className="tw-text-[14px] tw-text-gray-light-3"
                    />
                  </div>
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[8px]">
                    <p className="tw-m-0 tw-text-[18px] tw-font-medium tw-text-blue-dark-1">
                      <a
                        href="tel:+19293835362"
                        className="tw-font-bold tw-text-blue-dark-1 tw-no-underline"
                      >
                        (929) 383-5362
                      </a>
                    </p>
                    <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                      {getLocalizedHoursString(10)}, Monday – Friday.
                    </p>
                  </div>
                </div>
              </div>
              <div className="tw-min-h-0 tw-flex-1" aria-hidden />
            </div>

            <div className="tw-flex tw-w-full tw-justify-center">
              <img
                src={
                  rebrand
                    ? "/rebranded/homepage/img2.png"
                    : "/homepage/img2.png"
                }
                alt="Support team illustration"
                className="tw-h-auto tw-max-h-[220px] tw-w-full tw-max-w-[280px] tw-rounded-lg tw-object-contain"
              />
            </div>
          </div>

          {/* Patient support */}
          <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-[24px] tw-rounded-lg tw-border tw-border-solid tw-border-gray-light-1 tw-bg-white tw-p-[24px]">
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
              <div className="tw-flex tw-flex-col tw-gap-[28px]">
                <div className="tw-flex tw-flex-col tw-gap-[8px]">
                  <h2 className="tw-m-0 tw-font-degular tw-text-[28px] tw-font-medium tw-leading-none tw-tracking-[-0.28px] tw-text-blue-dark-1">
                    Patient support made easy
                  </h2>
                  <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                    Our dedicated patient experience team is here to help.{" "}
                    <TextButton
                      text={WHAT_TO_EXPECT_FROM_ORDER_TITLE}
                      onClick={() => {
                        analytics?.track(
                          "What To Expect From Order Modal Opened",
                        );
                        setShowExpectationsModal(true);
                      }}
                      className="tw-inline tw-p-0 tw-align-baseline tw-font-medium"
                      dataTestId="what-to-expect-from-order-open"
                    />
                  </p>
                  {showExpectationsModal && (
                    <WhatToExpectFromOrderModal
                      hideModal={() => setShowExpectationsModal(false)}
                    />
                  )}
                </div>

                <div className="tw-flex tw-items-start tw-gap-[8px]">
                  <div className="tw-flex tw-h-[32px] tw-w-[32px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-light-3">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="tw-text-[14px] tw-text-blue-dark-1"
                    />
                  </div>
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[8px]">
                    <p className="tw-m-0 tw-text-[18px] tw-font-medium tw-text-blue-dark-1">
                      <a
                        href="mailto:patient-support@versemedical.com"
                        className="tw-font-bold tw-text-blue-dark-1 tw-underline"
                      >
                        patient-support@versemedical.com
                      </a>
                    </p>
                    <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                      Best for returns, exchanges, and non-urgent questions.
                    </p>
                  </div>
                </div>

                <div className="tw-flex tw-items-start tw-gap-[8px]">
                  <div className="tw-flex tw-h-[32px] tw-w-[32px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-light-3">
                    <FontAwesomeIcon
                      icon={faPhoneFlip}
                      className="tw-text-[14px] tw-text-blue-dark-1"
                    />
                  </div>
                  <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[8px]">
                    <p className="tw-m-0 tw-text-[18px] tw-font-medium tw-text-blue-dark-1">
                      <a
                        href="tel:1-833-518-1613"
                        className="tw-font-bold tw-text-blue-dark-1 tw-no-underline"
                      >
                        (833) 518-1613
                      </a>
                    </p>
                    <p className="tw-m-0 tw-text-[14px] tw-font-medium tw-text-gray-2">
                      {getLocalizedHoursString(9)}, Monday – Friday. <br /> Save
                      this number so you don&apos;t miss important updates.
                    </p>
                  </div>
                </div>
              </div>
              <div className="tw-min-h-0 tw-flex-1" aria-hidden />
            </div>

            <div className="tw-flex tw-w-full tw-justify-center">
              <img
                src={
                  rebrand
                    ? "/rebranded/homepage/img6.png"
                    : "/homepage/img6.png"
                }
                alt="Patient support illustration"
                className="tw-h-auto tw-max-h-[220px] tw-w-full tw-max-w-[280px] tw-rounded-lg tw-object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Account Representative */}
      <AccountRepresentative />

      {/* CustomerServicePopup is rendered here because ProviderPortal
          (which normally mounts it) is not in the component tree on /contact_us. */}
      <CustomerServicePopup />

      {/* FAQ */}
      <div className="tw-rounded-lg tw-border tw-border-solid tw-border-gray-light-1 tw-p-[24px]">
        <div className="tw-flex tw-flex-col tw-gap-[24px]">
          <h2 className="tw-m-0 tw-font-degular tw-text-[28px] tw-font-medium tw-leading-none tw-tracking-[-0.28px] tw-text-blue-dark-1">
            Frequently Asked Questions
          </h2>
          {FAQ_ITEMS.map((item, index) => (
            <div key={index} className="tw-flex tw-flex-col">
              <button
                onClick={() =>
                  setOpenFaqIndex(openFaqIndex === index ? null : index)
                }
                className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-[16px] tw-border-none tw-bg-transparent tw-p-0 tw-text-left"
              >
                <i
                  className={`fas ${openFaqIndex === index ? "fa-chevron-up" : "fa-chevron-down"} tw-w-[16px] tw-flex-shrink-0 tw-text-[16px] tw-text-gray-4`}
                />
                <p className="tw-m-0 tw-text-[18px] tw-font-bold tw-text-gray-0">
                  {item.question}
                </p>
              </button>
              <div
                className={`tw-grid tw-transition-[grid-template-rows] tw-duration-300 ${openFaqIndex === index ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]"}`}
              >
                <div className="tw-overflow-hidden">
                  <p className="tw-m-0 tw-pl-[32px] tw-pt-[8px] tw-text-[14px] tw-font-medium tw-text-gray-2">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
